import type {
  AlertQuery,
  AuditQuery,
  DeviceQuery,
  DriverAccountQuery,
  DriverQuery,
  TransactionQuery,
  GeofenceQuery,
  SimQuery,
  TripQuery,
  UserQuery,
  VehicleQuery,
} from '@/lib/api/endpoints';
import type { Id, PaginationQuery } from '@/types/api';

/**
 * One place for every cache key. Sharing the prefixes means a mutation can
 * invalidate a whole resource (`queryKeys.vehicles.all`) without guessing.
 */
export const queryKeys = {
  session: ['session'] as const,

  vehicles: {
    all: ['vehicles'] as const,
    list: (query?: VehicleQuery) => ['vehicles', 'list', query ?? {}] as const,
    live: () => ['vehicles', 'live'] as const,
    detail: (id: Id) => ['vehicles', 'detail', id] as const,
    odometer: (id: Id) => ['vehicles', 'odometer', id] as const,
  },

  devices: {
    all: ['devices'] as const,
    list: (query?: DeviceQuery) => ['devices', 'list', query ?? {}] as const,
    detail: (id: Id) => ['devices', 'detail', id] as const,
    sims: (query?: SimQuery) => ['devices', 'sims', query ?? {}] as const,
  },

  telemetry: {
    all: ['telemetry'] as const,
    trail: (vehicleId: Id, range: { from?: string; to?: string; limit?: number }) =>
      ['telemetry', 'trail', vehicleId, range] as const,
    stats: (vehicleId: Id, range: { from: string; to: string }) =>
      ['telemetry', 'stats', vehicleId, range] as const,
  },

  trips: {
    all: ['trips'] as const,
    list: (query?: TripQuery) => ['trips', 'list', query ?? {}] as const,
    detail: (id: Id) => ['trips', 'detail', id] as const,
    utilization: (range: { from: string; to: string }) => ['trips', 'utilization', range] as const,
  },

  geofences: {
    all: ['geofences'] as const,
    list: (query?: GeofenceQuery) => ['geofences', 'list', query ?? {}] as const,
    detail: (id: Id) => ['geofences', 'detail', id] as const,
  },

  alerts: {
    all: ['alerts'] as const,
    list: (query?: AlertQuery) => ['alerts', 'list', query ?? {}] as const,
    summary: () => ['alerts', 'summary'] as const,
    detail: (id: Id) => ['alerts', 'detail', id] as const,
  },

  users: {
    all: ['users'] as const,
    list: (query?: UserQuery) => ['users', 'list', query ?? {}] as const,
    detail: (id: Id) => ['users', 'detail', id] as const,
  },

  roles: {
    all: ['roles'] as const,
    list: (query?: PaginationQuery) => ['roles', 'list', query ?? {}] as const,
  },

  maintenance: {
    all: ['maintenance'] as const,
    schedules: (query?: PaginationQuery & { vehicleId?: Id }) =>
      ['maintenance', 'schedules', query ?? {}] as const,
    records: (query?: PaginationQuery & { vehicleId?: Id }) =>
      ['maintenance', 'records', query ?? {}] as const,
    documents: (query?: PaginationQuery & { vehicleId?: Id }) =>
      ['maintenance', 'documents', query ?? {}] as const,
  },

  payments: {
    all: ['payments'] as const,
    summary: () => ['payments', 'summary'] as const,
    accounts: (query?: DriverAccountQuery) => ['payments', 'accounts', query ?? {}] as const,
    account: (id: Id) => ['payments', 'account', id] as const,
    transactions: (query?: TransactionQuery) =>
      ['payments', 'transactions', query ?? {}] as const,
  },

  drivers: {
    all: ['drivers'] as const,
    list: (query?: DriverQuery) => ['drivers', 'list', query ?? {}] as const,
  },

  simTracker: {
    all: ['sim-tracker'] as const,
    summary: () => ['sim-tracker', 'summary'] as const,
    sims: () => ['sim-tracker', 'sims'] as const,
  },

  commands: {
    all: ['commands'] as const,
    list: (query?: PaginationQuery & { vehicleId?: Id }) => ['commands', 'list', query ?? {}] as const,
  },

  audit: {
    all: ['audit'] as const,
    list: (query?: AuditQuery) => ['audit', 'list', query ?? {}] as const,
  },
} as const;
